import { Box, Divider, Stack, Typography, Avatar, List, ListItem, ListItemAvatar, ListItemText } from "@mui/material";
import TopHeadingDividerComponent from "../AccountSetting/TopHeadingDividerComponent";
import GroupsIcon from "../../UI/GroupsIcon";
import CreateIcon from "../../UI/CreateIcon";
import EventsIcon from "../../UI/EventsIcon";
import PagesIcon from "../../UI/PagesIcon";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../config/axios";
import SaveChangingsButton from "../AccountSetting/SaveChangingsButton";
import CommunityMenuIcon from "../../UI/CommunityMenuIcon";

const communityData = [
  { 
    icon: GroupsIcon, 
    title: "Groups",
    createRoute: "/community-group-form"
  },
  { 
    icon: EventsIcon, 
    title: "Events",
    createRoute: "/community-event-form"
  },
  { 
    icon: PagesIcon, 
    title: "Pages",
    createRoute: "/community"
  },
];

function CommunitySection() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommunityData();
  }, []);

  const fetchCommunityData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      if (!token) {
        console.error("No token found");
        return;
      }

      const [eventsRes, groupsRes, pagesRes] = await Promise.all([
        api.get("/events", {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get("/groups", {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get("/pages", {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setEvents(eventsRes.data || []);
      setGroups(groupsRes.data || []);
      setPages(pagesRes.data || []);
    } catch (error) {
      console.error("Error fetching community data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <Stack
      id="community"
      sx={{
        bgcolor: "#ffffff",
        borderRadius: "22px",
        p: "20px",
      }}
    >
      <TopHeadingDividerComponent
        primary="Community"
        secondary="Based on your profile, preferences, and activity like applies, searches, and saves"
        divider={false}
        primaryBottomPadding="4px"
        secondaryBottomPadding="0px"
      />
      <Divider
        sx={{
          height: "1px",
          bgcolor: "#EBEBEB",
          border: "none",
          my: "18px",
          mx: "-20px",
        }}
      />
      <Stack direction="row" sx={{ alignItems: "start", gap: "18px" }}>
        {communityData.map((community) => (
          <Stack
            key={community.title}
            sx={{
              border: "1px solid #E3E3E3",
              borderRadius: "24px",
              p: "20px",
              minWidth: "180px",
            }}
          >
            <community.icon />
            <Typography
              variant="body2"
              sx={{
                fontSize: "15px",
                fontWeight: "400",
                lineHeight: "100%",
                fontFamily: "Inter",
                py: "12px",
              }}
            >
              {community.title}
            </Typography>
            <Box
              component="button"
              onClick={() => navigate(community.createRoute)}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                p: "0px",
                cursor: "pointer",
                border: "none",
                bgcolor: "transparent",
                gap: "10px",
                pt: "20px",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            >
              <CreateIcon />
              <Typography
                variant="body2"
                sx={{
                  fontSize: "15px",
                  fontWeight: "400",
                  lineHeight: "100%",
                  fontFamily: "Inter",
                }}
              >
                Create
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      {/* Current Events Section */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: "500",
            mb: 2
          }}
        >
          Current Events
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: "#666",
            mb: 2
          }}
        >
          Based on your profile, preferences, and activity like applies, searches, and saves
        </Typography>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {events.map((event) => (
            <ListItem 
              key={event._id}
              sx={{ 
                p: "16px 0",
                borderBottom: "1px solid #EBEBEB"
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#EBEBEB' }}>
                  <EventsIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={event.name}
                secondary={formatDate(event.startDate)}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#000'
                  },
                  '& .MuiListItemText-secondary': {
                    fontSize: '14px',
                    color: '#666'
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Groups You've Joined Section */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: "500",
            mb: 2
          }}
        >
          Groups you've joined
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: "#666",
            mb: 2
          }}
        >
          Based on your profile, preferences, and activity like applies, searches, and saves
        </Typography>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {groups.map((group) => (
            <ListItem 
              key={group._id}
              sx={{ 
                p: "16px 0",
                borderBottom: "1px solid #EBEBEB"
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#EBEBEB' }}>
                  <GroupsIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={group.name}
                secondary={`Last active ${group.lastActive || 'recently'}`}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#000'
                  },
                  '& .MuiListItemText-secondary': {
                    fontSize: '14px',
                    color: '#666'
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <SaveChangingsButton mt="0">View Group</SaveChangingsButton>
                <Box
                  component="button"
                  sx={{
                    cursor: "pointer",
                    border: "none",
                    bgcolor: "transparent",
                    p: "0px",
                    height: "35px",
                  }}
                >
                  <CommunityMenuIcon />
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Pages You've Joined Section */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: "500",
            mb: 2
          }}
        >
          Pages you've joined
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: "#666",
            mb: 2
          }}
        >
          Based on your profile, preferences, and activity like applies, searches, and saves
        </Typography>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {pages.map((page) => (
            <ListItem 
              key={page._id}
              sx={{ 
                p: "16px 0",
                borderBottom: "1px solid #EBEBEB"
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#EBEBEB' }}>
                  <PagesIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={page.name}
                secondary={`Last active ${page.lastActive || 'recently'}`}
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: '16px',
                    color: '#000'
                  },
                  '& .MuiListItemText-secondary': {
                    fontSize: '14px',
                    color: '#666'
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <SaveChangingsButton mt="0">View Page</SaveChangingsButton>
                <Box
                  component="button"
                  sx={{
                    cursor: "pointer",
                    border: "none",
                    bgcolor: "transparent",
                    p: "0px",
                    height: "35px",
                  }}
                >
                  <CommunityMenuIcon />
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>
    </Stack>
  );
}

export default CommunitySection;
